import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Shape of the message pushed by the shared active-users service. */
export interface ActiveUsersMessage {
    message_type?: string;
    /** User IDs currently present on the same instance (project). */
    active_users: string[];
}

@Injectable({
    providedIn: 'root',
})
export class WebsocketService implements OnDestroy {
    private readonly messagesSubject = new Subject<any>();
    /** Binary YJS awareness frames received on the SOA edit socket. */
    private readonly awarenessSubject = new Subject<Uint8Array>();
    private socket: WebSocket | null = null;
    private readonly wsEndpoint = import.meta.env.NG_APP_WS_URL;

    // ─── Active users (presence) ────────────────────────────────────
    /** Delay before attempting to reconnect after an unexpected close. */
    private static readonly RECONNECT_DELAY_MS = 3000;

    private activeUsersSocket: WebSocket | null = null;
    private activeUsersReconnectTimer: ReturnType<typeof setTimeout> | null =
        null;
    private destroyed = false;
    private activeUsersParams: {
        userId: string;
        instanceId: string;
        appType: string;
    } | null = null;
    private readonly activeUsersSubject = new Subject<ActiveUsersMessage>();

    connect(
        project_num: string,
        soa_id: number,
        user_id: string
    ): Observable<any> {
        if (this.socket) {
            this.socket.close();
        }

        const wsUrl = `${this.wsEndpoint}ws/create_session?instance_id=${encodeURIComponent(project_num)}&soa_id=${encodeURIComponent(soa_id)}&user_id=${encodeURIComponent(user_id)}`;

        this.socket = new WebSocket(wsUrl);
        // Receive YJS awareness frames as binary rather than blobs.
        this.socket.binaryType = 'arraybuffer';

        this.socket.onmessage = (event) => {
            // Binary frame → YJS awareness (presence). Kept separate from the
            // JSON content-sync channel.
            if (event.data instanceof ArrayBuffer) {
                this.awarenessSubject.next(new Uint8Array(event.data));
                return;
            }

            const data = JSON.parse(event.data);

            // NEW: Check if it's the new message format
            if (data.type === 'room_updated' && data.data) {
                // data.data now contains the SendMessageRequest object: {room_id, username, content}
                // Transform it to match your Message interface
                this.messagesSubject.next({
                    message: data.data, // Contains: {room_id, username, content}
                    type: 'new_message',
                });
            } else {
                // Pass through other message types (connected, error, etc.)
                this.messagesSubject.next(data);
            }
        };

        this.socket.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        this.socket.onclose = () => {
            console.log('WebSocket closed');
        };

        return this.messagesSubject.asObservable();
    }

    /** Observable of incoming binary YJS awareness frames. */
    get awareness$(): Observable<Uint8Array> {
        return this.awarenessSubject.asObservable();
    }

    /** Expose the raw SOA edit socket so AwarenessService can send frames. */
    get rawSocket(): WebSocket | null {
        return this.socket;
    }

    disconnect() {
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
    }

    // ─── Active users (presence) ────────────────────────────────────

    /** Emits the latest presence list whenever the server pushes an update. */
    get activeUsers$(): Observable<ActiveUsersMessage> {
        return this.activeUsersSubject.asObservable();
    }

    /**
     * Open the active-users (presence) connection for an instance. The room
     * is keyed by `app_id = instance_id`; `app_type` identifies the module
     * (`soa` for SOA, `ec` for ECB). Reconnecting with the same parameters
     * is a no-op; different parameters replace the socket.
     */
    connectActiveUsers(
        userId: string,
        instanceId: string,
        appType: string
    ): void {
        if (!this.wsEndpoint || !userId || !instanceId || !appType) {
            return;
        }

        const sameConnection =
            this.activeUsersSocket &&
            this.activeUsersParams?.userId === userId &&
            this.activeUsersParams?.instanceId === instanceId &&
            this.activeUsersParams?.appType === appType;
        if (sameConnection) {
            return;
        }

        this.destroyed = false;
        this.activeUsersParams = { userId, instanceId, appType };
        this.openActiveUsersSocket();
    }

    /** Close the active-users connection and stop any pending reconnect. */
    disconnectActiveUsers(): void {
        this.clearActiveUsersReconnectTimer();
        this.activeUsersParams = null;
        this.closeActiveUsersSocket();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.disconnect();
        this.disconnectActiveUsers();
        this.activeUsersSubject.complete();
    }

    private openActiveUsersSocket(): void {
        if (!this.activeUsersParams) {
            return;
        }
        this.closeActiveUsersSocket();

        const { userId, instanceId, appType } = this.activeUsersParams;
        // The active-users (presence) room is served by the same WebSocket
        // host as the SOA edit socket, so it reuses NG_APP_WS_URL. The
        // endpoint already ends with a trailing slash (as in create_session).
        const url =
            `${this.wsEndpoint}ws/sdh/active_users` +
            `?user_id=${encodeURIComponent(userId)}` +
            `&app_type=${encodeURIComponent(appType)}` +
            `&app_id=${encodeURIComponent(instanceId)}`;

        const ws = new WebSocket(url);
        this.activeUsersSocket = ws;

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data) as ActiveUsersMessage;
                this.activeUsersSubject.next({
                    message_type: message.message_type,
                    active_users: message.active_users ?? [],
                });
            } catch {
                // Ignore malformed presence payloads.
            }
        };

        ws.onclose = () => {
            // Only reconnect if this socket is still the active one and the
            // service hasn't been torn down / explicitly disconnected.
            if (
                this.activeUsersSocket === ws &&
                !this.destroyed &&
                this.activeUsersParams
            ) {
                this.activeUsersSocket = null;
                this.scheduleActiveUsersReconnect();
            }
        };

        ws.onerror = () => {
            ws.close();
        };
    }

    private scheduleActiveUsersReconnect(): void {
        this.clearActiveUsersReconnectTimer();
        this.activeUsersReconnectTimer = setTimeout(() => {
            if (!this.destroyed && this.activeUsersParams) {
                this.openActiveUsersSocket();
            }
        }, WebsocketService.RECONNECT_DELAY_MS);
    }

    private clearActiveUsersReconnectTimer(): void {
        if (this.activeUsersReconnectTimer) {
            clearTimeout(this.activeUsersReconnectTimer);
            this.activeUsersReconnectTimer = null;
        }
    }

    private closeActiveUsersSocket(): void {
        if (this.activeUsersSocket) {
            // Detach handlers so closing doesn't trigger a reconnect.
            this.activeUsersSocket.onclose = null;
            this.activeUsersSocket.onerror = null;
            this.activeUsersSocket.onmessage = null;
            this.activeUsersSocket.close();
            this.activeUsersSocket = null;
        }
    }
}
